import {
  Checkbox,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type { LidarClearanceAxis } from "../schemas";
import t from "../translations";
import { axisValueOptions, type LabelNamesByAxis } from "./axisValueLabels";
import type { WizardEntityCheck, WizardRiskTier } from "./clearanceRulesConfig";
import RemoveIconButton from "./RemoveIconButton";

type Props = {
  check: WizardEntityCheck;
  axes: LidarClearanceAxis[];
  labelNames: LabelNamesByAxis;
  onChange: (check: WizardEntityCheck) => void;
  onRemove: () => void;
};

function TierFields({
  label,
  tier,
  axis,
  labelNames,
  onChange,
}: {
  label: string;
  tier: WizardRiskTier | null;
  axis: LidarClearanceAxis | undefined;
  labelNames: LabelNamesByAxis;
  onChange: (tier: WizardRiskTier | null) => void;
}) {
  const options = axis ? axisValueOptions(labelNames, axis) : [];
  const optionItems = options.map((option) => (
    <MenuItem key={option.value} value={option.value}>
      {option.label}
    </MenuItem>
  ));

  return (
    <Stack spacing={1}>
      <FormControlLabel
        control={
          <Checkbox
            checked={tier !== null}
            onChange={(event) =>
              onChange(event.target.checked ? { level: null, partWillHitLevel: null } : null)
            }
          />
        }
        label={label}
      />
      {tier !== null && (
        <Stack direction="row" spacing={1} sx={{ pl: 4 }}>
          <TextField
            select
            size="small"
            label={t.RiskTierLevelLabel}
            value={tier.level ?? ""}
            onChange={(event) =>
              onChange({ ...tier, level: Number(event.target.value) })
            }
            sx={{ width: 220 }}
          >
            {optionItems}
          </TextField>
          <TextField
            select
            size="small"
            label={t.RiskTierPartWillHitLevelLabel}
            value={tier.partWillHitLevel ?? ""}
            onChange={(event) =>
              onChange({
                ...tier,
                partWillHitLevel:
                  event.target.value === "" ? null : Number(event.target.value),
              })
            }
            sx={{ width: 260 }}
          >
            <MenuItem value="">{t.SameAsLevelOption}</MenuItem>
            {optionItems}
          </TextField>
        </Stack>
      )}
    </Stack>
  );
}

export default function EntityCheckCard({
  check,
  axes,
  labelNames,
  onChange,
  onRemove,
}: Props) {
  const axis = axes.find((axis) => axis.name_en === check.axis);

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid="entity-check-card">
      <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="subtitle2">{t.TreeRiskCheckLabel}</Typography>
        <RemoveIconButton aria-label={t.RemoveEntityCheckButton} onClick={onRemove} />
      </Stack>

      <TextField
        select
        size="small"
        label={t.AxisLabel}
        value={check.axis}
        onChange={(event) => onChange({ ...check, axis: event.target.value })}
        sx={{ width: 240, mb: 2 }}
      >
        {/* check.axis can reference an axis this system doesn't currently
            link (e.g. loaded from a hand-edited file, or copied from a
            system with an extra axis) - shown as-is rather than left blank,
            so this doesn't look unset and risk being silently overwritten
            by picking any axis from the list below. */}
        {axis === undefined && check.axis !== "" && (
          <MenuItem value={check.axis}>{check.axis}</MenuItem>
        )}
        {axes.map((option) => (
          <MenuItem key={option.id} value={option.name_en}>
            {option.name_en}
          </MenuItem>
        ))}
      </TextField>

      <TierFields
        label={t.RiskTreeTierLabel}
        tier={check.riskTree}
        axis={axis}
        labelNames={labelNames}
        onChange={(riskTree) => onChange({ ...check, riskTree })}
      />
      <TierFields
        label={t.LoneRiskTreeTierLabel}
        tier={check.loneRiskTree}
        axis={axis}
        labelNames={labelNames}
        onChange={(loneRiskTree) => onChange({ ...check, loneRiskTree })}
      />
    </Paper>
  );
}
